import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
    Home, FileText, CreditCard, LineChart, UserCircle2,
    ChevronLeft, Loader2, Crown, Trash2, AlertTriangle, X,
    Receipt, Ticket, Check,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PRIMARY = "#2D5BFF";
const PRIMARY_DARK = "#1B3DBF";
const GRADIENT = `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)`;

const NAV_ITEMS = [
    { icon: Home, label: "Home", key: "home" },
    { icon: FileText, label: "Receipts", key: "receipts" },
    { icon: CreditCard, label: "Cards", key: "cards" },
    { icon: LineChart, label: "Insights", key: "insights" },
    { icon: UserCircle2, label: "Profile", key: "profile" },
];

// Toggle pill component
const TogglePill = ({ options, value, onChange, disabled = false }) => (
    <div className="flex" style={{ borderRadius: 10, border: "1.5px solid #E5E7EB", overflow: "hidden" }}>
        {options.map((opt) => {
            const isActive = value === opt.value;
            return (
                <button
                    key={opt.value}
                    onClick={() => !disabled && onChange(opt.value)}
                    disabled={disabled}
                    style={{
                        padding: "8px 16px",
                        fontSize: 13, fontWeight: 600, fontFamily: "DM Sans, sans-serif",
                        backgroundColor: isActive ? "#111827" : "#FFFFFF",
                        color: isActive ? "#FFFFFF" : "#6B7280",
                        border: "none",
                        cursor: disabled ? "default" : "pointer",
                        transition: "all 0.15s",
                        opacity: disabled ? 0.6 : 1,
                    }}
                >
                    {opt.label}
                </button>
            );
        })}
    </div>
);

export default function MemberAccessPage() {
    const { groupId, memberId } = useParams();
    const { user: authUser } = useAuth();
    const navigate = useNavigate();
    const [isMobile, setIsMobile] = useState(false);
    const [loading, setLoading] = useState(true);
    const [group, setGroup] = useState(null);
    const [member, setMember] = useState(null);
    const [myRole, setMyRole] = useState(null);

    // Editable sharing settings (group-level, shown per-member for context)
    const [sharingReceipts, setSharingReceipts] = useState(true);
    const [sharingCards, setSharingCards] = useState(false);
    const [sharingVouchers, setSharingVouchers] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const [saving, setSaving] = useState(false);

    // Remove state
    const [confirmRemove, setConfirmRemove] = useState(false);
    const [removing, setRemoving] = useState(false);

    useEffect(() => {
        const check = () => setIsMobile(window.innerWidth < 768);
        check();
        window.addEventListener("resize", check);
        return () => window.removeEventListener("resize", check);
    }, []);

    const fetchData = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/groups/${groupId}`, { withCredentials: true });
            if (res.data.success) {
                setGroup(res.data.group);
                setMyRole(res.data.my_role);

                // Find the specific member
                const m = (res.data.members || []).find(mem => mem.id === memberId);
                setMember(m || null);

                // Set initial sharing values from group
                const sharing = res.data.group?.sharing || {};
                setSharingReceipts(sharing.receipts !== false);
                setSharingCards(!!sharing.loyalty_cards);
                setSharingVouchers(!!sharing.vouchers);
            }
        } catch (err) {
            if (err.response?.status === 404) {
                toast.error("Group not found");
                navigate("/profile", { replace: true });
            }
        } finally {
            setLoading(false);
        }
    }, [groupId, memberId, navigate]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const onNavClick = (key) => {
        if (key === "home") navigate("/dashboard");
        else if (key === "receipts") navigate("/receipts");
        else if (key === "cards") navigate("/cards");
        else if (key === "insights") navigate("/insights");
        else if (key === "profile") navigate("/profile");
    };

    // Track changes
    const updateSharing = (setter, value) => {
        setter(value);
        setHasChanges(true);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const res = await axios.put(`${API}/groups/${groupId}`, {
                sharing_loyalty_cards: sharingCards,
                sharing_vouchers: sharingVouchers,
            }, { withCredentials: true });
            if (res.data.success) {
                toast.success("Settings saved");
                setHasChanges(false);
            }
        } catch (err) {
            const detail = err.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Couldn't save settings");
        } finally {
            setSaving(false);
        }
    };

    const handleRemove = async () => {
        setRemoving(true);
        try {
            await axios.delete(`${API}/groups/${groupId}/members/${memberId}`, { withCredentials: true });
            toast.success(`${member?.display_name || "Member"} removed`);
            navigate(`/groups/${groupId}`, { replace: true });
        } catch (err) {
            const detail = err.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Couldn't remove member");
        } finally {
            setRemoving(false);
            setConfirmRemove(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#F9FAFB" }}>
                <Loader2 className="w-8 h-8 animate-spin" style={{ color: PRIMARY }} />
            </div>
        );
    }

    if (!group || !member) {
        return (
            <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#F9FAFB" }}>
                <p style={{ color: "#6B7280", fontSize: 14, fontFamily: "DM Sans, sans-serif" }}>Member not found</p>
            </div>
        );
    }

    const isMe = member.user_id === authUser?.id;
    const isOwner = member.role === "admin";
    const isAdmin = myRole === "admin";
    const memberName = member.display_name || member.email || "Unknown";
    const memberEmail = member.email || "";
    const initials = memberName.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);
    const colors = ["#7C3AED", "#2563EB", "#0D9488", "#D97706", "#DC2626", "#4F46E5", "#059669"];
    const colorIdx = (memberName.charCodeAt(0) + (memberName.charCodeAt(1) || 0)) % colors.length;
    const avatarColor = colors[colorIdx];

    // ── Shared page content ──────────────────────────────────────────────────
    const pageContent = () => (
        <div>
            {/* Member info */}
            <div className="flex items-center gap-3" style={{ marginBottom: 28 }}>
                <div
                    className="flex items-center justify-center overflow-hidden flex-shrink-0"
                    style={{
                        width: 52, height: 52, borderRadius: 999,
                        backgroundColor: member.avatar_url ? "transparent" : avatarColor,
                        color: "#FFFFFF", fontWeight: 700, fontSize: 18,
                        fontFamily: "Manrope, sans-serif",
                    }}
                >
                    {member.avatar_url ? (
                        <img src={member.avatar_url} alt={memberName} className="w-full h-full object-cover" />
                    ) : initials}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <p className="truncate" style={{ color: "#111827", fontSize: 18, fontWeight: 700, fontFamily: "Manrope, sans-serif" }}>
                            {memberName}{isMe ? " (You)" : ""}
                        </p>
                        {isOwner && (
                            <span
                                className="flex items-center gap-1 flex-shrink-0"
                                style={{
                                    padding: "3px 10px", borderRadius: 8,
                                    backgroundColor: "#FEF3C7", color: "#92400E",
                                    fontSize: 12, fontWeight: 700, fontFamily: "DM Sans, sans-serif",
                                }}
                            >
                                <Crown className="w-3.5 h-3.5" />
                                Owner
                            </span>
                        )}
                    </div>
                    {memberEmail && (
                        <p style={{ color: "#6B7280", fontSize: 14, fontFamily: "DM Sans, sans-serif", marginTop: 2 }}>
                            {memberEmail}
                        </p>
                    )}
                </div>
            </div>

            {/* Owner view — read-only */}
            {isOwner && isMe && (
                <div
                    style={{
                        padding: "16px 18px", borderRadius: 16,
                        backgroundColor: "#FFFBEB", border: "1.5px solid #FDE68A",
                    }}
                >
                    <div className="flex items-start gap-3">
                        <Crown className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: "#D97706" }} />
                        <p style={{ color: "#92400E", fontSize: 14, fontFamily: "DM Sans, sans-serif", lineHeight: 1.6 }}>
                            The owner has full access to everything and manages the circle.
                        </p>
                    </div>
                </div>
            )}

            {/* Non-owner view — permissions + actions */}
            {(!isOwner || !isMe) && (
                <>
                    {/* WHAT THEY CAN DO */}
                    <p style={{
                        color: PRIMARY, fontSize: 12, fontWeight: 700,
                        fontFamily: "DM Sans, sans-serif", letterSpacing: 1, marginBottom: 16,
                    }}>
                        WHAT THEY CAN DO
                    </p>

                    <div style={{
                        backgroundColor: "#FFFFFF", borderRadius: 20,
                        padding: "4px 18px",
                        boxShadow: "0 4px 16px rgba(15,23,42,0.06)",
                    }}>
                        {/* Receipts — always on */}
                        <div className="flex items-center justify-between py-3" style={{ borderBottom: "1px solid #F3F4F6" }}>
                            <div className="flex items-center gap-3">
                                <div className="flex items-center justify-center flex-shrink-0"
                                    style={{ width: 40, height: 40, borderRadius: 12, backgroundColor: "#EEF2FF" }}>
                                    <FileText className="w-5 h-5" style={{ color: PRIMARY }} />
                                </div>
                                <div>
                                    <p style={{ color: "#111827", fontSize: 15, fontWeight: 600, fontFamily: "Manrope, sans-serif" }}>Receipts</p>
                                    <p style={{ color: "#9CA3AF", fontSize: 12, fontFamily: "DM Sans, sans-serif", marginTop: 1 }}>
                                        Can view &amp; add
                                    </p>
                                </div>
                            </div>
                            <TogglePill
                                options={[
                                    { value: "view", label: "View" },
                                    { value: "contribute", label: "Contribute" },
                                ]}
                                value="contribute"
                                onChange={() => {}}
                                disabled={true}
                            />
                        </div>

                        {/* Loyalty cards */}
                        <div className="flex items-center justify-between py-3" style={{ borderBottom: "1px solid #F3F4F6" }}>
                            <div className="flex items-center gap-3">
                                <div className="flex items-center justify-center flex-shrink-0"
                                    style={{ width: 40, height: 40, borderRadius: 12, backgroundColor: "#EEF2FF" }}>
                                    <CreditCard className="w-5 h-5" style={{ color: PRIMARY }} />
                                </div>
                                <div>
                                    <p style={{ color: "#111827", fontSize: 15, fontWeight: 600, fontFamily: "Manrope, sans-serif" }}>Loyalty cards</p>
                                    <p style={{ color: "#9CA3AF", fontSize: 12, fontFamily: "DM Sans, sans-serif", marginTop: 1 }}>
                                        {sharingCards ? "Can view & add" : "Can view"}
                                    </p>
                                </div>
                            </div>
                            <TogglePill
                                options={[
                                    { value: "view", label: "View" },
                                    { value: "contribute", label: "Contribute" },
                                ]}
                                value={sharingCards ? "contribute" : "view"}
                                onChange={(v) => updateSharing(setSharingCards, v === "contribute")}
                                disabled={!isAdmin}
                            />
                        </div>

                        {/* Coupons & vouchers */}
                        <div className="flex items-center justify-between py-3" style={{ borderBottom: "1px solid #F3F4F6" }}>
                            <div className="flex items-center gap-3">
                                <div className="flex items-center justify-center flex-shrink-0"
                                    style={{ width: 40, height: 40, borderRadius: 12, backgroundColor: "#EEF2FF" }}>
                                    <Ticket className="w-5 h-5" style={{ color: PRIMARY }} />
                                </div>
                                <div>
                                    <p style={{ color: "#111827", fontSize: 15, fontWeight: 600, fontFamily: "Manrope, sans-serif" }}>Coupons &amp; vouchers</p>
                                    <p style={{ color: "#9CA3AF", fontSize: 12, fontFamily: "DM Sans, sans-serif", marginTop: 1 }}>
                                        {sharingVouchers ? "Can view & add" : "Can view"}
                                    </p>
                                </div>
                            </div>
                            <TogglePill
                                options={[
                                    { value: "view", label: "View" },
                                    { value: "contribute", label: "Contribute" },
                                ]}
                                value={sharingVouchers ? "contribute" : "view"}
                                onChange={(v) => updateSharing(setSharingVouchers, v === "contribute")}
                                disabled={!isAdmin}
                            />
                        </div>

                        {/* Group insights — always view */}
                        <div className="flex items-center justify-between py-3">
                            <div className="flex items-center gap-3">
                                <div className="flex items-center justify-center flex-shrink-0"
                                    style={{ width: 40, height: 40, borderRadius: 12, backgroundColor: "#EEF2FF" }}>
                                    <LineChart className="w-5 h-5" style={{ color: PRIMARY }} />
                                </div>
                                <div>
                                    <p style={{ color: "#111827", fontSize: 15, fontWeight: 600, fontFamily: "Manrope, sans-serif" }}>Circle insights</p>
                                    <p style={{ color: "#9CA3AF", fontSize: 12, fontFamily: "DM Sans, sans-serif", marginTop: 1 }}>
                                        See combined spending
                                    </p>
                                </div>
                            </div>
                            <TogglePill
                                options={[{ value: "view", label: "View" }]}
                                value="view"
                                onChange={() => {}}
                                disabled={true}
                            />
                        </div>
                    </div>

                    {/* Info note */}
                    {isAdmin && (
                        <div
                            className="flex items-start gap-2 mt-4"
                            style={{
                                padding: "14px 16px", borderRadius: 14,
                                backgroundColor: "#F0F4FF",
                            }}
                        >
                            <Check className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: PRIMARY }} />
                            <p style={{ color: "#374151", fontSize: 13, fontFamily: "DM Sans, sans-serif", lineHeight: 1.6 }}>
                                Contributors can add &amp; edit shared items and invite others. <strong>Only the person who added a receipt can delete it.</strong>
                            </p>
                        </div>
                    )}

                    {/* Save changes (admin only, when changes exist) */}
                    {isAdmin && hasChanges && (
                        <button
                            onClick={handleSave}
                            disabled={saving}
                            className="w-full flex items-center justify-center gap-2"
                            style={{
                                height: 52, borderRadius: 14, marginTop: 20,
                                background: GRADIENT, color: "#FFFFFF",
                                fontSize: 15, fontWeight: 700,
                                fontFamily: "DM Sans, sans-serif",
                                opacity: saving ? 0.7 : 1,
                                boxShadow: "0 8px 20px rgba(45,91,255,0.28)",
                            }}
                        >
                            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}
                            {saving ? "Saving…" : "Save changes"}
                        </button>
                    )}

                    {/* Remove from circle (admin only, not self) */}
                    {isAdmin && !isMe && (
                        <div style={{ marginTop: hasChanges ? 12 : 24 }}>
                            {!confirmRemove ? (
                                <button
                                    onClick={() => setConfirmRemove(true)}
                                    className="w-full flex items-center justify-center gap-2"
                                    style={{
                                        height: 48, borderRadius: 14,
                                        border: "1.5px solid #FECACA",
                                        backgroundColor: "#FFFFFF",
                                        color: "#EF4444", fontSize: 15, fontWeight: 600,
                                        fontFamily: "DM Sans, sans-serif",
                                    }}
                                >
                                    <Trash2 className="w-4 h-4" />
                                    Remove from circle
                                </button>
                            ) : (
                                <div style={{
                                    padding: 16, borderRadius: 14,
                                    border: "1.5px solid #FECACA", backgroundColor: "#FEF2F2",
                                }}>
                                    <div className="flex items-center gap-2 mb-3">
                                        <AlertTriangle className="w-4 h-4 flex-shrink-0" style={{ color: "#EF4444" }} />
                                        <p style={{ color: "#EF4444", fontSize: 13, fontWeight: 600, fontFamily: "DM Sans, sans-serif" }}>
                                            Remove {memberName} from this group? Their shared items will also be removed.
                                        </p>
                                    </div>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => setConfirmRemove(false)}
                                            className="flex-1 py-2.5 rounded-xl"
                                            style={{ border: "1px solid #E5E7EB", color: "#374151", fontSize: 14, fontWeight: 600, fontFamily: "DM Sans, sans-serif" }}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={handleRemove}
                                            disabled={removing}
                                            className="flex-1 py-2.5 rounded-xl flex items-center justify-center gap-2"
                                            style={{ backgroundColor: "#EF4444", color: "#FFFFFF", fontSize: 14, fontWeight: 600, fontFamily: "DM Sans, sans-serif" }}
                                        >
                                            {removing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                                            {removing ? "Removing…" : "Yes, remove"}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}
        </div>
    );

    // ── MOBILE LAYOUT ────────────────────────────────────────────────────────
    if (isMobile) {
        return (
            <div className="min-h-screen flex flex-col" style={{ backgroundColor: "#F9FAFB" }}>
                <header style={{ background: GRADIENT, padding: "16px 20px 24px 20px", borderBottomLeftRadius: 28, borderBottomRightRadius: 28 }}>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => navigate(-1)}
                            className="flex items-center justify-center"
                            style={{ width: 36, height: 36, borderRadius: 999, backgroundColor: "rgba(255,255,255,0.15)" }}
                        >
                            <ChevronLeft className="w-5 h-5" style={{ color: "#FFFFFF" }} />
                        </button>
                        <h1 style={{ color: "#FFFFFF", fontSize: 20, fontWeight: 700, fontFamily: "Manrope, sans-serif" }}>
                            Member access
                        </h1>
                    </div>
                </header>
                <div className="flex-1 px-4 pb-10" style={{ marginTop: 20 }}>
                    {pageContent()}
                </div>
            </div>
        );
    }

    // ── DESKTOP LAYOUT ───────────────────────────────────────────────────────
    const userName = `${authUser?.first_name || ""} ${authUser?.last_name || ""}`.trim() || "User";
    const avatarUrl = authUser?.avatar_url;
    const email = authUser?.email || "";

    return (
        <div className="min-h-screen flex" style={{ backgroundColor: "#F3F4F6" }}>
            <aside className="flex flex-col" style={{ width: 240, backgroundColor: "#F9FAFB", borderRight: "1px solid #F3F4F6" }}>
                <div className="flex items-center gap-2 px-5 pt-6 pb-8">
                    <svg width={28} height={28} viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg" style={{ borderRadius: 6, flexShrink: 0 }}>
                        <defs><linearGradient id="sb-g" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#4DD4E6" /><stop offset="100%" stopColor="#1B6CF5" /></linearGradient></defs>
                        <rect width="200" height="200" rx="44" fill="url(#sb-g)" />
                        <rect x="78" y="8" width="44" height="22" rx="8" fill="#3BBBD0" />
                        <circle cx="100" cy="95" r="38" fill="#FFF" />
                        <circle cx="106" cy="90" r="22" fill="#0F1B4D" />
                        <circle cx="114" cy="82" r="5" fill="#FFF" />
                        <path d="M72 140 Q100 164 128 140" stroke="#0F1B4D" strokeWidth="6" fill="none" strokeLinecap="round" />
                    </svg>
                    <span style={{ color: "#111827", fontSize: 18, fontWeight: 600, fontFamily: "Manrope, sans-serif" }}>My Buddy</span>
                </div>
                <nav className="flex-1 px-3">
                    {NAV_ITEMS.map((item) => (
                        <button key={item.key} onClick={() => onNavClick(item.key)} className="w-full flex items-center gap-3 px-4 py-3 mb-1 transition-all" style={{ backgroundColor: "transparent", color: "#6B7280", borderRadius: 10, fontSize: 14, fontWeight: 500, fontFamily: "DM Sans, sans-serif" }}>
                            <item.icon className="w-5 h-5" />
                            {item.label}
                        </button>
                    ))}
                </nav>
                <div className="p-4 border-t" style={{ borderColor: "#F3F4F6" }}>
                    <div className="flex items-center gap-3">
                        <div className="flex items-center justify-center overflow-hidden flex-shrink-0" style={{ width: 40, height: 40, borderRadius: 999, backgroundColor: avatarUrl ? "transparent" : PRIMARY, color: "#FFFFFF", fontWeight: 700, fontSize: 14, fontFamily: "Manrope, sans-serif" }}>
                            {avatarUrl ? <img src={avatarUrl} alt={userName} className="w-full h-full object-cover" /> : (userName || "U").charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                            <p className="truncate" style={{ color: "#111827", fontSize: 14, fontWeight: 600, fontFamily: "Manrope, sans-serif" }}>{userName}</p>
                            {email ? <p className="truncate" style={{ color: "#6B7280", fontSize: 12, fontFamily: "DM Sans, sans-serif" }}>{email}</p> : null}
                        </div>
                    </div>
                </div>
            </aside>

            <main className="flex-1 overflow-y-auto">
                <div className="flex items-center gap-3" style={{ padding: "24px 40px", borderBottom: "1px solid #E5E7EB", backgroundColor: "#FFFFFF" }}>
                    <button onClick={() => navigate(-1)} className="flex items-center justify-center" style={{ width: 36, height: 36, borderRadius: 10, backgroundColor: "#F3F4F6" }}>
                        <ChevronLeft className="w-5 h-5" style={{ color: "#6B7280" }} />
                    </button>
                    <h1 style={{ color: "#111827", fontSize: 22, fontWeight: 700, fontFamily: "Manrope, sans-serif" }}>Member access</h1>
                </div>
                <div style={{ maxWidth: 560, margin: "0 auto", padding: "30px 40px 40px" }}>
                    {pageContent()}
                </div>
            </main>
        </div>
    );
}